import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

const PushNotifications = () => {
  const [formData, setFormData] = useState({
    message: "",
    title: "",
    deeplink: "",
    image_url: "",
    mobile: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const blockedNumbers = [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);

    const mobileNumbers = formData.mobile
      .split(",")
      .map((num) => num.trim())
      .filter((num) => num !== "");

    const invalidNumbers = mobileNumbers.filter((num) =>
      blockedNumbers.includes(num),
    );

    if (invalidNumbers.length > 0) {
      toast.error(
        `Cannot send notifications to the following number(s): ${invalidNumbers.join(
          ", ",
        )}`,
      );
      setLoading(false);
      return;
    }

    try {
      const payload = { ...formData, mobile: mobileNumbers };

      // ✅ Using axiosConfig object form
      const response = await axiosConfig({
        method: "post",
        url: "/tnibroadmin/notification-triggered-to-single",
        data: payload,
      });

      const { status, response: respMessage, missing_mobiles } = response.data;

      if (status === "success") {
        toast.success(respMessage);

        if (missing_mobiles && missing_mobiles.length > 0) {
          toast.error(
            `Failed to send notification to: ${missing_mobiles.join(", ")}`,
          );
        }

        setFormData({
          message: "",
          title: "",
          deeplink: "",
          image_url: "",
          mobile: "",
        });
      } else {
        toast.error(respMessage || "Failed to send notification");
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.response ||
        error.message ||
        "Failed to send message";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <NotificationsActiveIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
        <Box>
          <Typography
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: 18,
              lineHeight: 1.2,
            }}
          >
            Push Notification
          </Typography>
          <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
            Send notification to specific mobile numbers
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          p: 3,
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          border: "1px solid #e2e8f0",
          maxWidth: 800,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Mobile (comma separated)"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              fullWidth
              size="small"
              required
            />
            <TextField
              label="Message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              fullWidth
              size="small"
              required
              multiline
              rows={3}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Deeplink (optional)"
                name="deeplink"
                value={formData.deeplink}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Image URL (optional)"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Box>
          </Box>

          <Box mt={3}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{
                background: "#016551",
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                py: 1.2,
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: "0 4px 20px rgba(26, 32, 53, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                  boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Send Notification"
              )}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default PushNotifications;
